
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Comment } from '@/types/social';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/auth';
import { Button } from '@/components/ui/button';
import { Reply } from 'lucide-react';
import CommentVoteBar from './CommentVoteBar';

interface CommentItemProps {
  comment: Comment;
  onReply?: (commentId: string) => void;
  userVotes: Record<string, { up?: boolean; down?: boolean }>;
  upvoteCounts: Record<string, number>;
  downvoteCounts: Record<string, number>;
  isVoting: Record<string, boolean>;
  handleVote: (commentId: string, voteType: 'up' | 'down') => void;
  maxVotes: number;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onReply,
  userVotes,
  upvoteCounts,
  downvoteCounts,
  isVoting,
  handleVote,
  maxVotes
}) => {
  const { user } = useAuth();
  
  // Safely access vote counts with fallbacks to prevent runtime errors
  const upvoteCount = upvoteCounts[comment.id] || 0;
  const downvoteCount = downvoteCounts[comment.id] || 0;
  const hasUpvoted = userVotes[comment.id]?.up || false;
  const hasDownvoted = userVotes[comment.id]?.down || false;
  const isVotingActive = isVoting[comment.id] || false;
  
  return (
    <div className="space-y-2">
      <div className="flex items-start space-x-3">
        <Avatar className="h-8 w-8">
          <AvatarImage src={comment.profile?.avatar_url || ""} />
          <AvatarFallback>{comment.profile?.name?.charAt(0).toUpperCase() || 'NC'}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium leading-none">{comment.profile?.name || 'Anonymous'}</div>
              <div className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
              </div>
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-300">{comment.content}</p>
          </div>
          
          <div className="flex items-center mt-2 gap-4">
            <CommentVoteBar
              commentId={comment.id}
              upvoteCount={upvoteCount}
              downvoteCount={downvoteCount}
              maxVotes={maxVotes}
              hasUpvoted={hasUpvoted}
              hasDownvoted={hasDownvoted}
              isVoting={isVotingActive}
              onVote={handleVote}
            />
            
            {/* Add reply button if onReply handler is provided */}
            {onReply && (
              <Button 
                variant="ghost" 
                size="sm"
                className="p-1 h-6"
                onClick={() => onReply(comment.id)}
              >
                <Reply className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">Reply</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
